import React, { useEffect, useState, useRef } from "react";
import { keyframes } from "@emotion/react";

import {
  Alert,
  Avatar,
  Chip,
  InputAdornment,
  TextField,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  LinearProgress,
  CircularProgress,
  Slide,
} from "@mui/material";

import { useParams, useNavigate, useLocation } from "react-router-dom";

import { styled } from "@mui/material/styles";
import DocumentStatus from "../../../componentes/DocumentStatus";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import DownloadIcon from "@mui/icons-material/Download";

import { api, fetchDocumentoPdf } from "@api/api";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";

const StyledPaper = styled(Paper)(() => ({
  width: "100%",
  padding: 0,
  borderRadius: 0,
  backgroundColor: "transparent",
  boxShadow: "none",
}));

const formatSeiNumber = (seiNumber) => {
  if (!seiNumber) return "";

  const cleaned = String(seiNumber).replace(/\D/g, "");
  let formatted = "";

  if (cleaned.length > 0) formatted += cleaned.substring(0, 4);
  if (cleaned.length > 4) formatted += "." + cleaned.substring(4, 6);
  if (cleaned.length > 6) formatted += "." + cleaned.substring(6, 13);
  if (cleaned.length > 13) formatted += "/" + cleaned.substring(13, 17);
  if (cleaned.length > 17) formatted += "-" + cleaned.substring(17, 19);

  return formatted;
};

const formatarDataSemFuso = (dataString) => {
  if (!dataString) return "Não informado";

  const [ano, mes, dia] = String(dataString).slice(0, 10).split("-");

  return `${dia}/${mes}/${ano}`;
};

const normalizarTexto = (valor = "") =>
  String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const piscarDocumento = keyframes`
  0%, 100% {
    background-color: #eef7ff;
    box-shadow: 0 0 0 0 rgba(23, 111, 178, 0.15);
  }
  50% {
    background-color: #d2e9fc;
    box-shadow: 0 0 0 5px rgba(23, 111, 178, 0.3);
  }
`;

const DetalhesProcesso = () => {
  const { processId } = useParams();
  const navigate = useNavigate();
  const { search, key: chaveNavegacao } = useLocation();

  const documentoNotificado = new URLSearchParams(search).get("documento");

  const [detalhes, setDetalhes] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [buscaDocumento, setBuscaDocumento] = useState("");
  const [fileStates, setFileStates] = useState({});

  const inputFileRefs = useRef([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [documentToView, setDocumentToView] = useState(null);
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [openRecusaDialog, setOpenRecusaDialog] = useState(false);
  const [motivoRecusa, setMotivoRecusa] = useState("");

  const [openHistorico, setOpenHistorico] = useState(false);
  const [historicoDevolucoes, setHistoricoDevolucoes] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState("");
  const [documentoHistorico, setDocumentoHistorico] = useState(null);

  const [documentosVisualizados, setDocumentosVisualizados] = useState(
    () => new Set(),
  );

  useEffect(() => {
    setBuscaDocumento("");
    setDocumentosVisualizados(new Set());
  }, [processId, documentoNotificado, chaveNavegacao]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("token");

        const [processResponse, documentsResponse] = await Promise.all([
          fetch(api.getProcess(processId), {
            method: "GET",
            signal: controller.signal,
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "Content-Type": "application/json",
            },
            credentials: "include",
          }),
          fetch(api.getProcessDocs(processId), {
            method: "GET",
            signal: controller.signal,
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "Content-Type": "application/json",
            },
            credentials: "include",
          }),
        ]);

        if (!processResponse.ok) {
          throw new Error("Erro ao buscar detalhes do processo");
        }

        if (!documentsResponse.ok) {
          throw new Error("Erro ao buscar documentos do processo");
        }

        const [processData, documentsData] = await Promise.all([
          processResponse.json(),
          documentsResponse.json(),
        ]);

        if (!Array.isArray(documentsData)) {
          throw new Error("Resposta inválida ao buscar documentos do processo");
        }

        if (controller.signal.aborted) return;

        setDetalhes(processData);
        setDocuments(documentsData);
        setFileStates(
          documentsData.map(() => ({
            uploaded: false,
          })),
        );
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => controller.abort();
  }, [processId, refreshKey, chaveNavegacao]);

  const handleFileChange = async (index, event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Apenas arquivos PDF são permitidos!");

      if (inputFileRefs.current[index]) {
        inputFileRefs.current[index].value = "";
      }

      return;
    }

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append("file", file);

      const response = await fetch(api.updateDoc(documents[index].id), {
        method: "PATCH",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        throw new Error(errorData.message || "Erro ao atualizar documento");
      }

      setRefreshKey((prevKey) => prevKey + 1);

      if (inputFileRefs.current[index]) {
        inputFileRefs.current[index].value = "";
      }
    } catch (err) {
      console.error("Erro no upload:", err);
      alert(err.message || "Erro ao atualizar documento");
    }
  };

  const handleRecusaClick = (document) => {
    setDocumentosVisualizados((anteriores) =>
      new Set(anteriores).add(String(document.id)),
    );

    setMotivoRecusa(document.observacao || "");
    setOpenRecusaDialog(true);
  };

  const handleOpenHistorico = async (document) => {
    setDocumentoHistorico(document);
    setHistoricoDevolucoes([]);
    setErroHistorico("");
    setLoadingHistorico(true);
    setOpenHistorico(true);

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(api.userDocumentHistory(document.id), {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(
          errorText || "Erro ao carregar o histórico do documento",
        );
      }

      const data = await response.json();

      setHistoricoDevolucoes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);

      setErroHistorico("Não foi possível carregar o histórico do documento.");
    } finally {
      setLoadingHistorico(false);
    }
  };

  const handleCloseHistorico = () => {
    setOpenHistorico(false);
    setHistoricoDevolucoes([]);
    setErroHistorico("");
    setDocumentoHistorico(null);
  };

  const handleBaixarAnexo = async (registro) => {
    if (!registro?.possui_anexo) {
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(api.userHistoryAttachment(registro.id), {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Erro ao baixar o anexo");
      }

      const arquivoBlob = await response.blob();
      const urlTemporaria = URL.createObjectURL(arquivoBlob);

      const link = document.createElement("a");
      link.href = urlTemporaria;
      link.download = registro.anexo_nome || "anexo";

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(urlTemporaria);
    } catch (error) {
      console.error("Erro ao baixar anexo:", error);
      setErroHistorico("Não foi possível baixar o anexo.");
    }
  };

  const handleClick = (index) => {
    if (!inputFileRefs.current[index]) {
      const inputElement = document.createElement("input");

      inputElement.type = "file";
      inputElement.style.display = "none";
      inputElement.accept = "application/pdf";

      inputElement.onchange = (event) => handleFileChange(index, event);

      document.body.appendChild(inputElement);
      inputFileRefs.current[index] = inputElement;
    }

    inputFileRefs.current[index].click();
  };

  const handleOpenDocument = (document) => {
    setDocumentosVisualizados((anteriores) =>
      new Set(anteriores).add(String(document.id)),
    );

    setDocumentToView(document);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDocumentToView(null);
  };

  useEffect(() => {
    if (!openDialog || !documentToView?.id) {
      setDocumentUrl("");
      setDocumentError("");
      setLoadingDocument(false);
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = "";

    const carregarDocumento = async () => {
      setLoadingDocument(true);
      setDocumentError("");
      setDocumentUrl("");

      try {
        const blob = await fetchDocumentoPdf(
          api.getDocumentoConteudo(documentToView.id),
          {
            signal: controller.signal,
          },
        );

        objectUrl = URL.createObjectURL(blob);
        setDocumentUrl(objectUrl);
      } catch (error) {
        if (error.name !== "AbortError") {
          setDocumentError(error.message);
          setDocumentUrl("");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingDocument(false);
        }
      }
    };

    carregarDocumento();

    return () => {
      controller.abort();

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [openDialog, documentToView]);

  const documentosFiltrados = documents
    .map((document, indexOriginal) => ({
      document,
      indexOriginal,
      destacado: String(document.id) === documentoNotificado,
    }))
    .filter(({ document }) =>
      normalizarTexto(document.nome_documento).includes(
        normalizarTexto(buscaDocumento),
      ),
    )
    .sort((a, b) => Number(b.destacado) - Number(a.destacado));

  if (loading) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Carregando detalhes do processo...
          </Typography>

          <LinearProgress color="primary" />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <TransicaoPagina variante="navegacao">
        <Container maxWidth={false} disableGutters sx={{ mt: 3 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" color="error" sx={{ mb: 2 }}>
              Erro: {error}
            </Typography>

            <Button variant="contained" onClick={() => navigate(-1)}>
              Voltar
            </Button>
          </Box>
        </Container>
      </TransicaoPagina>
    );
  }

  if (!detalhes) {
    return (
      <TransicaoPagina variante="navegacao">
        <Container maxWidth={false} disableGutters sx={{ mt: 3 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h5" color="error" sx={{ mb: 2 }}>
              Detalhes do processo não encontrados.
            </Typography>

            <Button variant="contained" onClick={() => navigate(-1)}>
              Voltar
            </Button>
          </Box>
        </Container>
      </TransicaoPagina>
    );
  }

  return (
    <TransicaoPagina variante="navegacao">
      <Container maxWidth={false} disableGutters sx={{ mt: 4, mb: 4 }}>
        <StyledPaper elevation={0}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate(-1)}
              sx={{
                minHeight: 42,
                paddingX: 2,
                color: "#0767d8",
                borderColor: "#087cf0",
                backgroundColor: "#ffffff",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  color: "#ffffff",
                  borderColor: "#087cf0",
                  backgroundColor: "#087cf0",
                  boxShadow: "none",
                },
              }}
            >
              Voltar aos processos
            </Button>

            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ color: "#0767d8", fontWeight: 700 }}
              >
                Detalhes do processo
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: "#5f6f94", marginTop: 0.5 }}
              >
                Acompanhe os documentos e as pendências deste processo.
              </Typography>
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              padding: { xs: 2, md: 2.5 },
              marginBottom: 3,
              border: "1px solid #9fcdff",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 58,
                  height: 58,
                  flexShrink: 0,
                  color: "#087cf0",
                  backgroundColor: "#eaf4ff",
                }}
              >
                <DescriptionOutlinedIcon />
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "#0767d8",
                    fontWeight: 700,
                    marginBottom: 1.5,
                  }}
                >
                  {detalhes.processtype}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <SchoolRoundedIcon
                      sx={{ color: "#087cf0", fontSize: 21 }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                        Escola
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#33446b" }}>
                        {detalhes.schoolname || "Não informado"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <LocationOnOutlinedIcon
                      sx={{ color: "#087cf0", fontSize: 21 }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                        Município
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#33446b" }}>
                        {detalhes.state || "Não informado"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CalendarMonthOutlinedIcon
                      sx={{ color: "#087cf0", fontSize: 21 }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                        Data de início
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#33446b" }}>
                        {formatarDataSemFuso(detalhes.startdate)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DescriptionOutlinedIcon
                      sx={{ color: "#087cf0", fontSize: 21 }}
                    />

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#5f6f94" }}>
                        Nº SEI
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#33446b",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {formatSeiNumber(detalhes.seinumber) || "Não informado"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              border: "1px solid #9fcdff",
              borderRadius: 2,
              backgroundColor: "#ffffff",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                padding: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#0767d8", fontWeight: 700 }}
                >
                  Documentos do processo
                </Typography>

                <Chip
                  label={documents.length}
                  size="small"
                  sx={{
                    color: "#0767d8",
                    backgroundColor: "#eaf4ff",
                    fontWeight: 700,
                  }}
                />
              </Box>

              <TextField
                value={buscaDocumento}
                onChange={(event) => setBuscaDocumento(event.target.value)}
                placeholder="Pesquisar documento..."
                size="small"
                sx={{
                  width: { xs: "100%", md: 325 },
                  "& .MuiOutlinedInput-root": {
                    minHeight: 42,
                    borderRadius: 2,
                    "& fieldset": { borderColor: "#9fcdff" },
                    "&:hover fieldset": { borderColor: "#087cf0" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#087cf0",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "#5f6f94" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#eaf4ff" }}>
                    <TableCell
                      sx={{
                        color: "#3156a4",
                        fontWeight: 700,
                        paddingY: 1.5,
                        borderBottom: "1px solid #c9def4",
                      }}
                    >
                      Nome
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "#3156a4",
                        fontWeight: 700,
                        paddingY: 1.5,
                        borderBottom: "1px solid #c9def4",
                      }}
                    >
                      Status
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "#3156a4",
                        fontWeight: 700,
                        paddingY: 1.5,
                        borderBottom: "1px solid #c9def4",
                      }}
                    >
                      Correção
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "#3156a4",
                        fontWeight: 700,
                        paddingY: 1.5,
                        borderBottom: "1px solid #c9def4",
                      }}
                    >
                      Visualizar
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {documentosFiltrados.length > 0 ? (
                    documentosFiltrados.map(
                      ({ document, indexOriginal, destacado }) => (
                        <TableRow
                          key={`${document.id}-${refreshKey}`}
                          hover
                          sx={{
                            bgcolor: destacado ? "#eef7ff" : undefined,
                            "&:nth-of-type(odd)": {
                              bgcolor: destacado ? "#eef7ff" : "action.hover",
                            },
                            outline: destacado
                              ? "2px solid #176fb2"
                              : undefined,
                            outlineOffset: "-2px",
                            animation:
                              destacado &&
                              !documentosVisualizados.has(String(document.id))
                                ? `${piscarDocumento} 1.4s ease-in-out infinite`
                                : "none",
                            transition: "background-color 0.3s ease",
                            "@media (prefers-reduced-motion: reduce)": {
                              animation: "none",
                            },
                          }}
                        >
                          <TableCell>
                            {destacado && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  color: "#176fb2",
                                  fontWeight: 700,
                                }}
                              >
                                Documento devolvido para correção
                              </Typography>
                            )}

                            {document.nome_documento}
                          </TableCell>

                          <TableCell>
                            <DocumentStatus
                              aprovacao={document.aprovacao}
                              alteracao_ativa={document.alteracao_ativa}
                            />
                          </TableCell>

                          <TableCell>
                            {document.alteracao_ativa === false ? (
                              <Button
                                variant="contained"
                                disabled
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                  boxShadow: "none",
                                }}
                              >
                                Envio bloqueado
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Button
                                  variant="contained"
                                  color={
                                    fileStates[indexOriginal]?.uploaded
                                      ? "secondary"
                                      : "primary"
                                  }
                                  onClick={() => handleClick(indexOriginal)}
                                  disabled={
                                    fileStates[indexOriginal]?.uploaded ||
                                    !document.alteracao_ativa
                                  }
                                  sx={{
                                    borderRadius: "20px",
                                    textTransform: "none",
                                    boxShadow: "none",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      transform: "translateY(-2px)",
                                    },
                                  }}
                                >
                                  {fileStates[indexOriginal]?.uploaded
                                    ? "Enviado ✓"
                                    : "Enviar"}
                                </Button>

                                {document.observacao && (
                                  <Button
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => handleRecusaClick(document)}
                                    sx={{
                                      borderRadius: "20px",
                                      textTransform: "none",
                                      boxShadow: "none",
                                    }}
                                  >
                                    Motivo da recusa
                                  </Button>
                                )}
                              </Box>
                            )}
                          </TableCell>

                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleOpenDocument(document)}
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    backgroundColor: "primary.main",
                                    color: "common.white",
                                  },
                                }}
                              >
                                Visualizar
                              </Button>

                              {/*
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleOpenHistorico(document)}
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    backgroundColor: "primary.main",
                                    color: "common.white",
                                  },
                                }}
                              >
                                Histórico
                              </Button>
                              */}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Nenhum documento encontrado para a pesquisa.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </StyledPaper>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          TransitionComponent={Slide}
          transitionDuration={400}
          sx={{
            "& .MuiDialog-paper": {
              height: "90vh",
              maxHeight: "90vh",
              borderRadius: "12px",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "primary.main",
              color: "common.white",
              fontWeight: "bold",
            }}
          >
            Visualizar Documento
          </DialogTitle>

          <DialogContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              padding: 0,
              overflow: "hidden",
            }}
          >
            {loadingDocument ? (
              <CircularProgress />
            ) : documentError ? (
              <Alert severity="error">{documentError}</Alert>
            ) : documentUrl ? (
              <iframe
                src={documentUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Documento Visualizado"
              />
            ) : null}
          </DialogContent>

          <DialogActions sx={{ bgcolor: "background.default" }}>
            <Button
              onClick={handleCloseDialog}
              color="primary"
              variant="contained"
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: "bold",
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openHistorico}
          onClose={handleCloseHistorico}
          fullWidth
          maxWidth="md"
        >
          <DialogContent>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Histórico de devoluções
            </Typography>

            {documentoHistorico && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Documento: {documentoHistorico.nome_documento}
              </Typography>
            )}

            {loadingHistorico ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <CircularProgress />
              </Box>
            ) : erroHistorico ? (
              <Alert severity="error">{erroHistorico}</Alert>
            ) : historicoDevolucoes.length === 0 ? (
              <Alert severity="info">
                Nenhuma devolução registrada para este documento.
              </Alert>
            ) : (
              <Box>
                {historicoDevolucoes.map((registro, index) => (
                  <Box
                    key={registro.id}
                    sx={{
                      py: 2,
                      borderBottom:
                        index < historicoDevolucoes.length - 1
                          ? "1px solid #e0e0e0"
                          : "none",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {registro.analista_nome || "Analista não identificado"}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      {registro.data_hora
                        ? new Date(registro.data_hora).toLocaleString("pt-BR", {
                            timeZone: "America/Sao_Paulo",
                          })
                        : "Data não informada"}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                    >
                      {registro.motivo}
                    </Typography>

                    {registro.possui_anexo && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleBaixarAnexo(registro)}
                        sx={{ mt: 1, textTransform: "none" }}
                      >
                        {registro.anexo_nome || "Baixar anexo"}
                      </Button>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseHistorico} variant="outlined">
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openRecusaDialog}
          onClose={() => setOpenRecusaDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Motivo da recusa
            </Typography>

            <Typography variant="body1">{motivoRecusa}</Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setOpenRecusaDialog(false)}
              variant="outlined"
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </TransicaoPagina>
  );
};

export default DetalhesProcesso;
